import { ArrowBack } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  ChipProps,
  FormControlLabel,
  LinearProgress,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { format, parseISO } from 'date-fns';
import { useMemo, useState } from 'react';

import { TERMINATION_REASON_CODES } from '../../utils/constants';

interface TerminationSubmitStepProps {
  rows: TerminationRow[];
  results: BulkTerminationRowResult[] | null;
  isSubmitting: boolean;
  submitProgress: number;
  onBack: () => void;
  onExecute: () => void;
  onStartOver: () => void;
}

function resultChip(result: BulkTerminationRowResult | undefined): {
  label: string;
  color: ChipProps['color'];
} {
  if (!result) return { label: 'Pending', color: 'default' };
  if (result.status === 'success')
    return { label: 'Terminated', color: 'success' };
  return { label: 'Failed', color: 'error' };
}

const TerminationSubmitStep: React.FC<TerminationSubmitStepProps> = ({
  rows,
  results,
  isSubmitting,
  submitProgress,
  onBack,
  onExecute,
  onStartOver,
}) => {
  const [confirmed, setConfirmed] = useState(false);
  const eligibleRows = useMemo(() => rows.filter((r) => !r.errors), [rows]);
  const isComplete = results !== null;

  const summary = useMemo(() => {
    if (!results) return null;
    const succeeded = results.filter((r) => r.status === 'success').length;
    const failed = results.filter((r) => r.status === 'error').length;
    return { succeeded, failed, total: results.length };
  }, [results]);

  const gridRows = useMemo(() => {
    if (results) {
      return results.map((r, i) => ({ ...r, id: i }));
    }
    return eligibleRows.map((r, i) => ({ ...r, id: i }));
  }, [results, eligibleRows]);

  const columns = useMemo<GridColDef[]>(
    () => [
      {
        field: 'employee_id',
        headerName: 'Employee ID',
        flex: 1,
        minWidth: 100,
      },
      ...(results
        ? [
            {
              field: 'status',
              headerName: 'Result',
              flex: 1,
              minWidth: 120,
              sortable: false,
              renderCell: (params: GridRenderCellParams) => {
                const chip = resultChip(params.row as BulkTerminationRowResult);
                return (
                  <Chip
                    label={chip.label}
                    color={chip.color}
                    size="small"
                    sx={{ fontWeight: 600 }}
                  />
                );
              },
            } as GridColDef,
            {
              field: 'message',
              headerName: 'Details',
              flex: 2,
              minWidth: 200,
            } as GridColDef,
          ]
        : ([
            {
              field: 'term_status_code',
              headerName: 'Status Code',
              flex: 0.8,
              minWidth: 80,
            },
            {
              field: 'reason_code',
              headerName: 'Reason',
              flex: 1.2,
              minWidth: 160,
              valueFormatter: (params: { value: string }) =>
                TERMINATION_REASON_CODES[
                  params.value as keyof typeof TERMINATION_REASON_CODES
                ] ?? params.value,
            },
            {
              field: 'term_date',
              headerName: 'Term Date',
              flex: 0.9,
              minWidth: 90,
              valueFormatter: (params: { value: string }) => {
                if (!params.value) return '';
                try {
                  return format(parseISO(params.value), 'MM-dd-yyyy');
                } catch {
                  return params.value;
                }
              },
            },
          ] as GridColDef[])),
    ],
    [results]
  );

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
          {isComplete ? 'Termination Results' : 'Confirm & Submit'}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {isComplete
            ? `Processed ${summary?.total} employee${
                summary?.total !== 1 ? 's' : ''
              }.`
            : `You are about to terminate ${eligibleRows.length} employee${
                eligibleRows.length !== 1 ? 's' : ''
              }. This action cannot be undone.`}
        </Typography>
      </Box>

      {isComplete && summary && (
        <Box sx={{ display: 'flex', gap: 3, mb: 3 }}>
          {[
            { value: summary.total, label: 'Total', color: 'text.primary' },
            {
              value: summary.succeeded,
              label: 'Succeeded',
              color: 'success.main',
            },
            {
              value: summary.failed,
              label: 'Failed',
              color: summary.failed > 0 ? 'error.main' : 'text.primary',
            },
          ].map((s, i) => (
            <Box key={s.label} sx={{ display: 'flex', alignItems: 'center' }}>
              {i > 0 && (
                <Box
                  sx={{
                    width: '1px',
                    height: 32,
                    backgroundColor: 'divider',
                    mr: 3,
                  }}
                />
              )}
              <Box sx={{ textAlign: 'center' }}>
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 700, color: s.color }}
                >
                  {s.value}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {s.label}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {!isComplete && (
        <Alert
          severity="warning"
          variant="outlined"
          sx={{ mb: 2, py: 0.5, borderRadius: 1 }}
        >
          <Typography variant="caption" sx={{ fontWeight: 600 }}>
            Terminations are permanent. Please review carefully before
            proceeding.
          </Typography>
        </Alert>
      )}

      {isSubmitting && (
        <Box sx={{ mb: 2 }}>
          <Box
            sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}
          >
            <Typography variant="caption" color="text.secondary">
              Processing terminations...
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 600 }}
            >
              {submitProgress}%
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={submitProgress}
            sx={{ height: 6, borderRadius: 3 }}
          />
        </Box>
      )}

      <DataGrid
        rows={gridRows}
        columns={columns}
        loading={isSubmitting}
        density="compact"
        disableRowSelectionOnClick
        initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
        pageSizeOptions={[25, 50, 100]}
        getRowClassName={(params) => {
          if (!results) return '';
          return params.row.status === 'error' ? 'row--error' : 'row--success';
        }}
        sx={{
          height: 400,
          backgroundColor: 'white',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          '& .MuiDataGrid-cell:focus': { outline: 'none' },
          '& .MuiDataGrid-columnHeader:focus': { outline: 'none' },
          '& .row--error': {
            backgroundColor: 'error.lighter',
            borderLeft: '3px solid',
            borderLeftColor: 'error.main',
          },
          '& .row--success': {
            backgroundColor: 'success.lighter',
          },
        }}
      />

      <Box
        sx={{
          mt: 3,
          pt: 2.5,
          borderTop: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        {isComplete ? (
          <>
            <Button
              onClick={onStartOver}
              variant="outlined"
              startIcon={<ArrowBack />}
            >
              Start Over
            </Button>
          </>
        ) : (
          <>
            <Button
              onClick={onBack}
              variant="outlined"
              startIcon={<ArrowBack />}
              disabled={isSubmitting}
            >
              Back to Review
            </Button>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={confirmed}
                    onChange={(e) => setConfirmed(e.target.checked)}
                    disabled={isSubmitting}
                  />
                }
                label={
                  <Typography variant="body2">
                    {`I confirm I want to terminate ${
                      eligibleRows.length
                    } employee${eligibleRows.length !== 1 ? 's' : ''}`}
                  </Typography>
                }
              />
              <LoadingButton
                variant="contained"
                color="error"
                onClick={onExecute}
                disabled={!confirmed}
                loading={isSubmitting}
                size="large"
              >
                {`Terminate ${eligibleRows.length} Employee${
                  eligibleRows.length !== 1 ? 's' : ''
                }`}
              </LoadingButton>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

export default TerminationSubmitStep;
